import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { useState, useEffect, useRef } from "react";
import {
  Users, Banknote, Briefcase, GraduationCap, CalendarClock,
  Sparkles, Shield, Zap, ArrowRight, Check, Clock,
  BarChart3, FileText, Brain, HeartPulse, MessageSquare,
  Award, TrendingUp, Timer, ChevronDown, Star, Globe,
  Rocket, Lock, Headphones, Palette, Play, ChevronRight,
  Target, ClipboardList, Network, Gift, Laptop, FolderOpen,
  MousePointerClick, Bot, LayoutDashboard, CircleDot, Smartphone, Download, MapPin
} from "lucide-react";

/* ====== SCROLL REVEAL HOOK ====== */
const useReveal = (threshold = 0.15) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

/* ====== COUNTER ====== */
const Counter = ({ end, suffix = "", prefix = "" }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        let s = 0; const step = Math.max(1, Math.floor(end / 40));
        const t = setInterval(() => { s += step; if (s >= end) { setCount(end); clearInterval(t); } else setCount(s); }, 30);
        obs.disconnect();
      }
    });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [end]);
  return <span ref={ref}>{prefix}{count.toLocaleString("es-MX")}{suffix}</span>;
};

/* ====== DATA ====== */
const MODULES = [
  { icon: Users, name: "Empleados", desc: "Directorio, perfiles y expedientes digitales", color: "from-emerald-500 to-teal-600" },
  { icon: Banknote, name: "Nomina", desc: "Calculos automaticos y cumplimiento SAT", color: "from-blue-500 to-blue-700" },
  { icon: Briefcase, name: "Reclutamiento", desc: "Vacantes, pipeline y analisis de CVs con IA", color: "from-violet-500 to-purple-700" },
  { icon: Brain, name: "Psicometria IA", desc: "Evaluaciones psicometricas automatizadas", color: "from-pink-500 to-rose-600" },
  { icon: GraduationCap, name: "Capacitaciones", desc: "Cursos, progreso y certificaciones", color: "from-orange-500 to-amber-600" },
  { icon: Award, name: "Desempeno 360", desc: "Evaluaciones y planes de desarrollo", color: "from-teal-500 to-cyan-600" },
  { icon: CalendarClock, name: "Control Horario", desc: "Jornadas, turnos y horas extra", color: "from-cyan-500 to-sky-600" },
  { icon: Timer, name: "Asistencia", desc: "Registro, permisos y calendario", color: "from-rose-500 to-red-600" },
  { icon: Clock, name: "Vacaciones", desc: "Solicitudes, aprobaciones y saldos", color: "from-amber-500 to-yellow-600" },
  { icon: Rocket, name: "Onboarding", desc: "Integracion de nuevos colaboradores", color: "from-indigo-500 to-violet-600" },
  { icon: FolderOpen, name: "Documentos", desc: "Expediente digital y firma electronica", color: "from-slate-500 to-gray-600" },
  { icon: Laptop, name: "Activos", desc: "Control de equipo y herramientas", color: "from-stone-500 to-zinc-600" },
  { icon: Headphones, name: "Helpdesk", desc: "Mesa de ayuda y tickets", color: "from-sky-500 to-blue-600" },
  { icon: Shield, name: "Denuncias", desc: "Canal confidencial NOM-035", color: "from-red-500 to-rose-700" },
  { icon: ClipboardList, name: "Encuestas", desc: "Clima laboral y NOM-035", color: "from-lime-500 to-green-600" },
  { icon: Globe, name: "Comunicacion", desc: "Avisos, noticias y comunicados", color: "from-fuchsia-500 to-pink-600" },
  { icon: Gift, name: "Beneficios", desc: "Programas de bienestar", color: "from-pink-600 to-rose-700" },
  { icon: BarChart3, name: "Analytics", desc: "Metricas y KPIs en tiempo real", color: "from-blue-600 to-indigo-700" },
  { icon: Sparkles, name: "Informes IA", desc: "Reportes generados con inteligencia artificial", color: "from-violet-600 to-purple-700" },
  { icon: Network, name: "Organigrama", desc: "Estructura organizacional interactiva", color: "from-emerald-600 to-green-700" },
];

const PLANS = [
  { id: "estandar", name: "Estandar", price: 89, modules: 7, color: "border-slate-200", bg: "bg-white", features: ["Gestion de personal", "Directorio y organigrama", "Vacaciones e incidencias", "Expediente digital", "Comunicacion interna", "Mesa de ayuda", "App movil"] },
  { id: "avanzado", name: "Avanzado", price: 129, modules: 12, color: "border-emerald-400", bg: "bg-white", popular: true, features: ["Todo de Estandar", "Nomina y timbrado", "Control de asistencia", "Onboarding automatizado", "Control de activos", "Canal de denuncias NOM-035", "Firma electronica"] },
  { id: "pro", name: "Pro", price: 159, modules: 20, color: "border-violet-400", bg: "bg-white", features: ["Todo de Avanzado", "Reclutamiento con IA", "Psicometria con IA", "Evaluacion 360", "Capacitaciones", "Encuestas NOM-035", "Analytics y reportes IA", "4 Agentes de IA"] },
];

const TESTIMONIALS = [
  { name: "Maria Gonzalez", role: "Directora de RH", company: "TechMX Corp", quote: "Redujimos un 60% el tiempo dedicado a procesos manuales. El modulo de nomina es excepcional.", avatar: "MG" },
  { name: "Carlos Ramirez", role: "CEO", company: "InnovaDigital", quote: "La IA para reclutamiento nos ahorro semanas en cada proceso de seleccion. Impresionante.", avatar: "CR" },
  { name: "Ana Torres", role: "Gerente de RH", company: "Grupo Industrial MX", quote: "El cumplimiento NOM-035 ahora es automatico. Nos olvidamos de las preocupaciones regulatorias.", avatar: "AT" },
];

const Landing = () => {
  const [showAll, setShowAll] = useState(false);
  const [numEmp, setNumEmp] = useState(25);
  const [heroRef, heroVis] = useReveal(0.1);
  const [statsRef, statsVis] = useReveal();
  const [modRef, modVis] = useReveal();
  const [aiRef, aiVis] = useReveal();
  const [priceRef, priceVis] = useReveal();
  const [testRef, testVis] = useReveal();

  return (
    <div className="min-h-screen bg-[#fafbfc] overflow-x-hidden">
      {/* ============ NAV ============ */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/70 backdrop-blur-xl border-b border-slate-100/80" data-testid="landing-header">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo-humanoz.png" alt="Human-OZ" className="h-9 w-auto" />
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-[13px] font-medium text-slate-500">
            <a href="#modulos" className="hover:text-slate-900 transition-colors">Modulos</a>
            <a href="#precios" className="hover:text-slate-900 transition-colors">Precios</a>
            <a href="#ia" className="hover:text-slate-900 transition-colors">IA</a>
            <a href="#testimonios" className="hover:text-slate-900 transition-colors">Testimonios</a>
          </nav>
          <div className="flex items-center gap-3">
            <Link to="/login"><Button variant="ghost" size="sm" className="text-slate-600 font-medium" data-testid="header-login-btn">Iniciar Sesion</Button></Link>
            <Link to="/register"><Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-5 font-semibold shadow-md shadow-emerald-600/20" data-testid="header-register-btn">Prueba Gratis</Button></Link>
          </div>
        </div>
      </header>

      {/* ============ HERO ============ */}
      <section ref={heroRef} className="pt-24 pb-0 relative overflow-hidden" style={{ background: "linear-gradient(180deg, #0a0f1a 0%, #111827 60%, #0d1424 100%)" }}>
        {/* Animated orbs */}
        <div className="absolute top-20 left-1/4 w-[500px] h-[500px] rounded-full bg-emerald-500/10 blur-[120px] animate-pulse" />
        <div className="absolute top-40 right-1/4 w-[400px] h-[400px] rounded-full bg-violet-500/10 blur-[100px]" style={{ animation: "pulse 4s ease-in-out infinite" }} />
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] rounded-full bg-blue-500/5 blur-[80px]" />

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className={`max-w-3xl mx-auto text-center transition-all duration-1000 ${heroVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-semibold mb-8 tracking-wide">
              <CircleDot className="w-3 h-3 animate-pulse" />
              NUEVO: 20 modulos + 4 Agentes de IA
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] font-extrabold text-white leading-[1.1] mb-6 tracking-tight">
              Automatiza tu departamento{" "}
              <br className="hidden sm:block" />
              de RH con{" "}
              <span className="relative inline-block">
                <span className="bg-gradient-to-r from-emerald-400 via-teal-300 to-cyan-400 bg-clip-text text-transparent">Inteligencia Artificial</span>
                <svg className="absolute -bottom-2 left-0 w-full" viewBox="0 0 300 12" fill="none"><path d="M2 8C50 2 100 2 150 6C200 10 250 4 298 8" stroke="url(#ug)" strokeWidth="3" strokeLinecap="round"/><defs><linearGradient id="ug" x1="0" y1="0" x2="300" y2="0"><stop stopColor="#34d399"/><stop offset="1" stopColor="#22d3ee"/></linearGradient></defs></svg>
              </span>
            </h1>
            <p className="text-base sm:text-lg text-slate-400 mb-3 max-w-xl mx-auto leading-relaxed">
              Cada hora que tu equipo pierde en procesos manuales es dinero que no recuperas.
              <strong className="text-white"> Human-OZ automatiza todo desde $89/empleado/mes.</strong>
            </p>
            <p className="text-amber-400/90 font-semibold text-sm mb-8 flex items-center justify-center gap-2">
              <Clock className="w-4 h-4" />
              15 dias gratis con TODOS los modulos. Sin tarjeta de credito.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/register">
                <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 font-bold h-13 px-8 rounded-full text-base shadow-xl shadow-emerald-500/25 transition-all hover:scale-[1.03] active:scale-95" data-testid="hero-cta-btn">
                  Comenzar Prueba Gratis
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="#modulos">
                <Button size="lg" variant="ghost" className="h-13 px-6 rounded-full text-slate-400 hover:text-white hover:bg-white/5">
                  <Play className="w-4 h-4 mr-2" />Ver Modulos
                </Button>
              </a>
            </div>
            <div className="flex items-center justify-center gap-6 mt-8 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400" />Sin tarjeta</span>
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400" />Setup en 5 min</span>
              <span className="flex items-center gap-1.5"><Check className="w-3.5 h-3.5 text-emerald-400" />Cancela cuando quieras</span>
            </div>
          </div>

          {/* ============ DASHBOARD MOCKUP ============ */}
          <div className={`mt-16 max-w-5xl mx-auto transition-all duration-1000 delay-300 ${heroVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="relative">
              {/* Glow behind mockup */}
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-500/10 to-violet-500/5 rounded-t-2xl blur-2xl scale-95" />
              {/* Browser chrome */}
              <div className="relative bg-[#1a1f2e] rounded-t-2xl border border-white/10 shadow-2xl overflow-hidden">
                <div className="flex items-center gap-2 px-5 py-3 border-b border-white/5">
                  <div className="flex gap-1.5"><div className="w-2.5 h-2.5 rounded-full bg-red-500/80" /><div className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" /><div className="w-2.5 h-2.5 rounded-full bg-green-500/80" /></div>
                  <div className="flex-1 flex justify-center"><div className="bg-white/5 rounded-md px-4 py-1 text-[11px] text-slate-500 font-mono">app.humanoz.click/dashboard</div></div>
                </div>
                {/* Fake dashboard */}
                <div className="p-6 bg-gradient-to-br from-[#0f1521] to-[#141b2a]">
                  <div className="grid grid-cols-12 gap-4">
                    {/* Sidebar mini */}
                    <div className="col-span-2 space-y-3 hidden md:block">
                      <div className="h-6 w-20 bg-emerald-500/20 rounded-md" />
                      {[...Array(7)].map((_, i) => <div key={i} className="h-3 bg-white/5 rounded w-full" style={{ width: `${60 + Math.random() * 40}%` }} />)}
                    </div>
                    {/* Main area */}
                    <div className="col-span-12 md:col-span-10 space-y-4">
                      <div className="grid grid-cols-4 gap-3">
                        {[
                          { label: "Empleados", val: "247", c: "from-emerald-500/20 to-emerald-500/5 border-emerald-500/20" },
                          { label: "Asistencia", val: "96.2%", c: "from-blue-500/20 to-blue-500/5 border-blue-500/20" },
                          { label: "Vacaciones", val: "12", c: "from-amber-500/20 to-amber-500/5 border-amber-500/20" },
                          { label: "Nomina", val: "$1.2M", c: "from-violet-500/20 to-violet-500/5 border-violet-500/20" },
                        ].map((s, i) => (
                          <div key={i} className={`bg-gradient-to-br ${s.c} border rounded-xl p-3`}>
                            <p className="text-[10px] text-slate-400">{s.label}</p>
                            <p className="text-lg font-bold text-white mt-1">{s.val}</p>
                          </div>
                        ))}
                      </div>
                      <div className="grid grid-cols-3 gap-3">
                        <div className="col-span-2 bg-white/[0.03] border border-white/5 rounded-xl p-4 h-32">
                          <p className="text-xs text-slate-400 mb-3">Tendencia de asistencia</p>
                          <div className="flex items-end gap-1.5 h-16">
                            {[65,72,80,68,85,90,78,92,88,95,82,97].map((h, i) => (
                              <div key={i} className="flex-1 bg-gradient-to-t from-emerald-500/60 to-emerald-400/20 rounded-t-sm" style={{ height: `${h}%` }} />
                            ))}
                          </div>
                        </div>
                        <div className="bg-white/[0.03] border border-white/5 rounded-xl p-4 h-32">
                          <p className="text-xs text-slate-400 mb-3">Por departamento</p>
                          <div className="space-y-2 mt-2">
                            {[{ w: "80%", l: "TI" }, { w: "65%", l: "Ventas" }, { w: "45%", l: "MKT" }].map((d, i) => (
                              <div key={i}><p className="text-[9px] text-slate-500 mb-0.5">{d.l}</p><div className="h-2 rounded-full bg-white/5"><div className="h-full rounded-full bg-gradient-to-r from-violet-500 to-blue-500" style={{ width: d.w }} /></div></div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* Fade to next section */}
        <div className="h-20 bg-gradient-to-b from-[#0d1424] to-[#fafbfc]" />
      </section>

      {/* ============ TICKER ============ */}
      <section className="py-8 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <p className="text-center text-xs font-medium text-slate-400 uppercase tracking-widest mb-6">La plataforma de RH mas completa de Mexico</p>
          <div className="flex flex-wrap justify-center gap-4">
            {["20+ Modulos", "4 Agentes IA", "NOM-035", "Timbrado SAT", "Firma Digital", "White Label", "API Abierta"].map((t, i) => (
              <span key={i} className="px-4 py-2 rounded-full bg-slate-100 text-slate-600 text-xs font-semibold border border-slate-200/80 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 transition-all cursor-default">{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* ============ STATS BAR ============ */}
      <section ref={statsRef} className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <div className={`grid grid-cols-2 md:grid-cols-4 gap-8 transition-all duration-700 ${statsVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {[
              { val: 20, suf: "+", label: "Modulos integrados", icon: LayoutDashboard, color: "text-emerald-500" },
              { val: 4, suf: "", label: "Agentes de IA", icon: Bot, color: "text-violet-500" },
              { val: 70, suf: "%", label: "Menos tiempo en procesos", icon: Zap, color: "text-amber-500" },
              { val: 100, suf: "%", label: "Cumplimiento NOM-035", icon: Shield, color: "text-blue-500" },
            ].map((s, i) => (
              <div key={i} className="text-center group">
                <div className={`w-12 h-12 mx-auto rounded-2xl bg-slate-100 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform ${s.color}`}>
                  <s.icon className="w-5 h-5" />
                </div>
                <p className="text-3xl font-extrabold text-slate-900 tracking-tight"><Counter end={s.val} suffix={s.suf} /></p>
                <p className="text-sm text-slate-500 mt-1">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PAIN POINTS - VISUAL ============ */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <Badge className="bg-red-100 text-red-700 border-red-200 mb-4 font-semibold">El problema</Badge>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
              Tu departamento de RH sigue trabajando asi?
            </h2>
            <p className="text-slate-500 text-base max-w-xl mx-auto">El 78% de las empresas mexicanas pierden mas de 40 horas/mes en procesos manuales de RH</p>
          </div>
          <div className="grid md:grid-cols-3 gap-5">
            {[
              { pain: "Hojas de Excel para nomina", fix: "Nomina automatizada con timbrado SAT", icon: Banknote },
              { pain: "Vacaciones por WhatsApp", fix: "Portal de autoservicio para empleados", icon: Clock },
              { pain: "Reclutamiento sin filtro", fix: "IA que pre-filtra y evalua candidatos", icon: Brain },
            ].map((item, i) => (
              <div key={i} className="group rounded-2xl border border-slate-200 overflow-hidden hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <div className="p-6 bg-gradient-to-br from-red-50 to-orange-50 border-b border-red-100">
                  <div className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center mb-3">
                    <item.icon className="w-5 h-5 text-red-500" />
                  </div>
                  <p className="text-red-700 font-semibold text-sm">{item.pain}</p>
                </div>
                <div className="p-6 bg-gradient-to-br from-emerald-50 to-teal-50">
                  <div className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                    <p className="text-emerald-700 font-semibold text-sm">{item.fix}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-12 p-8 rounded-2xl bg-slate-50 border border-slate-200">
            <p className="text-slate-500 text-sm mb-2">El costo de NO automatizar para una empresa de 30 empleados:</p>
            <p className="text-4xl font-extrabold text-red-600 tracking-tight">$187,000 MXN/ano</p>
            <p className="text-xs text-slate-400 mt-1">En horas-hombre desperdiciadas, errores de nomina y rotacion evitable</p>
          </div>
        </div>
      </section>

      {/* ============ BENTO MODULES ============ */}
      <section id="modulos" ref={modRef} className="py-20 bg-[#fafbfc]">
        <div className="max-w-7xl mx-auto px-6">
          <div className={`text-center mb-14 transition-all duration-700 ${modVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 mb-4 font-semibold">20 Modulos</Badge>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
              Todo lo que necesitas. En un solo lugar.
            </h2>
            <p className="text-base text-slate-500 max-w-2xl mx-auto">Cada modulo resuelve un problema real. Todos incluidos en tu prueba gratuita.</p>
          </div>
          <div className={`grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3 transition-all duration-700 delay-200 ${modVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {(showAll ? MODULES : MODULES.slice(0, 10)).map((m, i) => (
              <div key={i} className="bg-white rounded-xl p-4 border border-slate-200/80 hover:shadow-lg hover:-translate-y-1 transition-all duration-200 cursor-default group" data-testid={`module-card-${i}`}>
                <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${m.color} flex items-center justify-center mb-3 group-hover:scale-110 transition-transform shadow-sm`}>
                  <m.icon className="w-5 h-5 text-white" />
                </div>
                <h3 className="font-bold text-[13px] text-slate-900">{m.name}</h3>
                <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">{m.desc}</p>
              </div>
            ))}
          </div>
          {!showAll && (
            <div className="text-center mt-8">
              <Button variant="outline" onClick={() => setShowAll(true)} className="rounded-full border-slate-300 hover:bg-emerald-50 hover:text-emerald-700 hover:border-emerald-200 transition-all" data-testid="show-all-modules">
                Ver los 20 modulos <ChevronDown className="w-4 h-4 ml-1" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* ============ AI AGENTS - VISUAL ============ */}
      <section id="ia" ref={aiRef} className="py-24 relative overflow-hidden" style={{ background: "#0c0a1d" }}>
        <div className="absolute inset-0"><div className="absolute top-0 left-1/3 w-[500px] h-[500px] rounded-full bg-violet-600/8 blur-[150px]" /><div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] rounded-full bg-blue-600/5 blur-[120px]" /></div>
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className={`text-center mb-16 transition-all duration-700 ${aiVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Badge className="bg-violet-500/15 text-violet-300 border-violet-500/20 mb-4 font-semibold">Potenciado por IA</Badge>
            <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight mb-4">
              4 Agentes de IA que trabajan<br /><span className="text-violet-400">mientras tu descansas</span>
            </h2>
          </div>
          <div className={`grid md:grid-cols-2 lg:grid-cols-4 gap-5 transition-all duration-700 delay-200 ${aiVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {[
              { name: "Reclutador IA", desc: "Analiza CVs, genera descripciones de puesto, pre-filtra candidatos y agenda entrevistas", icon: Briefcase, stat: "80% menos tiempo", gradient: "from-violet-600 to-indigo-700" },
              { name: "Nomina IA", desc: "Valida calculos, detecta errores antes del timbrado, verifica cumplimiento fiscal", icon: Banknote, stat: "0 errores de nomina", gradient: "from-blue-600 to-cyan-700" },
              { name: "Compliance IA", desc: "Monitorea NOM-035, alerta sobre vencimientos, genera documentos regulatorios", icon: Shield, stat: "100% en cumplimiento", gradient: "from-emerald-600 to-teal-700" },
              { name: "Engagement IA", desc: "Predice rotacion, analiza clima laboral, sugiere acciones de retencion", icon: HeartPulse, stat: "40% menos rotacion", gradient: "from-rose-600 to-pink-700" },
            ].map((agent, i) => (
              <div key={i} className="group relative rounded-2xl border border-white/[0.06] bg-white/[0.03] backdrop-blur-sm p-6 hover:bg-white/[0.06] transition-all duration-300 hover:-translate-y-1">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${agent.gradient} flex items-center justify-center mb-5 shadow-lg group-hover:scale-110 transition-transform`}>
                  <agent.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{agent.name}</h3>
                <p className="text-sm text-slate-400 mb-5 leading-relaxed">{agent.desc}</p>
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-500/10 border border-emerald-500/20">
                  <Zap className="w-3 h-3 text-emerald-400" />
                  <span className="text-emerald-400 text-xs font-semibold">{agent.stat}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PROCESS STEPS ============ */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <Badge className="bg-blue-100 text-blue-700 border-blue-200 mb-4 font-semibold">Facil de usar</Badge>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
              Activo en 3 pasos
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { step: "01", title: "Registrate gratis", desc: "Crea tu cuenta en 2 minutos. Sin tarjeta, sin compromiso. Acceso inmediato al plan Pro.", icon: MousePointerClick, color: "bg-emerald-500" },
              { step: "02", title: "Configura tu empresa", desc: "Importa empleados por CSV o agreglos manualmente. Personaliza modulos segun tus necesidades.", icon: Users, color: "bg-blue-500" },
              { step: "03", title: "Automatiza todo", desc: "Deja que la IA trabaje por ti: nomina, reclutamiento, compliance y engagement en automatico.", icon: Sparkles, color: "bg-violet-500" },
            ].map((s, i) => (
              <div key={i} className="relative group">
                <div className="text-center">
                  <div className={`w-16 h-16 ${s.color} rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg group-hover:scale-110 transition-transform`}>
                    <s.icon className="w-7 h-7 text-white" />
                  </div>
                  <span className="text-xs font-bold text-slate-300 uppercase tracking-widest">Paso {s.step}</span>
                  <h3 className="text-xl font-bold text-slate-900 mt-2 mb-3">{s.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{s.desc}</p>
                </div>
                {i < 2 && <ChevronRight className="hidden md:block absolute top-12 -right-6 w-6 h-6 text-slate-300" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ TESTIMONIALS ============ */}
      <section id="testimonios" ref={testRef} className="py-20 bg-[#fafbfc]">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`text-center mb-14 transition-all duration-700 ${testVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Badge className="bg-amber-100 text-amber-700 border-amber-200 mb-4 font-semibold">Testimonios</Badge>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
              Lo que dicen nuestros clientes
            </h2>
          </div>
          <div className={`grid md:grid-cols-3 gap-6 transition-all duration-700 delay-200 ${testVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1" data-testid={`testimonial-${i}`}>
                <div className="flex gap-1 mb-4">{[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-amber-400 text-amber-400" />)}</div>
                <p className="text-slate-700 text-sm leading-relaxed mb-6 italic">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white font-bold text-sm shadow-sm">{t.avatar}</div>
                  <div>
                    <p className="font-semibold text-sm text-slate-900">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.role}, {t.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ PRICING ============ */}
      <section id="precios" ref={priceRef} className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className={`text-center mb-12 transition-all duration-700 ${priceVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 mb-4 font-semibold">Precios transparentes</Badge>
            <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
              Invierte menos de lo que pierdes
            </h2>
            <p className="text-base text-slate-500">Precio por empleado/mes. Sin contratos forzosos.</p>
            <div className="flex items-center justify-center gap-4 mt-6">
              <span className="text-sm text-slate-500">Empleados:</span>
              <input type="range" min="5" max="200" value={numEmp} onChange={(e) => setNumEmp(parseInt(e.target.value))} className="w-40 accent-emerald-500" data-testid="employee-range" />
              <span className="font-extrabold text-lg text-slate-900 bg-slate-100 px-3 py-1 rounded-lg min-w-[3rem] text-center">{numEmp}</span>
            </div>
          </div>
          <div className={`grid md:grid-cols-3 gap-5 max-w-5xl mx-auto transition-all duration-700 delay-200 ${priceVis ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {PLANS.map((plan) => (
              <div key={plan.id} className={`rounded-2xl border-2 ${plan.color} ${plan.bg} p-6 relative transition-all duration-300 hover:shadow-xl ${plan.popular ? "shadow-xl ring-2 ring-emerald-500/20 scale-[1.03] z-10" : "hover:-translate-y-1"}`} data-testid={`plan-card-${plan.id}`}>
                {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2"><Badge className="bg-emerald-600 text-white px-4 py-1 shadow-lg">Mas Popular</Badge></div>}
                <div className="text-center mb-6 pt-2">
                  <h3 className="text-lg font-bold text-slate-900">{plan.name}</h3>
                  <p className="text-xs text-slate-500 mt-1">{plan.modules} modulos</p>
                  <div className="mt-4 flex items-baseline justify-center gap-1">
                    <span className="text-sm text-slate-400">$</span>
                    <span className="text-4xl font-extrabold text-slate-900 tracking-tight">{plan.price}</span>
                    <span className="text-sm text-slate-400">/emp/mes</span>
                  </div>
                  <p className="text-sm font-bold text-emerald-600 mt-2">${(plan.price * numEmp).toLocaleString("es-MX")}/mes total</p>
                </div>
                <ul className="space-y-2.5 mb-6">
                  {plan.features.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-emerald-500 shrink-0 mt-0.5" />{f}</li>)}
                </ul>
                <Link to="/register">
                  <Button className={`w-full rounded-full h-11 font-semibold transition-all ${plan.popular ? "bg-emerald-600 hover:bg-emerald-700 text-white shadow-md shadow-emerald-600/20" : "bg-slate-100 hover:bg-slate-200 text-slate-700"}`} data-testid={`price-${plan.id}`}>
                    Probar 15 dias gratis
                  </Button>
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-xs text-slate-400 mt-8">Todos los precios en MXN + IVA. La prueba gratis incluye todos los modulos del plan Pro.</p>
        </div>
      </section>

      {/* ============ WHITE LABEL ============ */}
      <section id="whitelabel" className="py-24 overflow-hidden" style={{ background: "#0c0a1d" }}>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="bg-violet-500/15 text-violet-300 border-violet-500/20 mb-6 font-semibold">Para consultoras y empresas</Badge>
              <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight mb-6">
                White Label:{" "}
                <span className="text-violet-400">Tu marca, tu sistema</span>
              </h2>
              <p className="text-base text-slate-400 mb-8 leading-relaxed">Obtente Human-OZ como si fuera tu propio software. Tu logo, tus colores, tu dominio. Tus clientes nunca sabran que es Human-OZ.</p>
              <div className="space-y-3">
                {[
                  { label: "Hasta 20 empleados", price: "$30,000 MXN/ano" },
                  { label: "Hasta 50 empleados", price: "$60,000 MXN/ano" },
                  { label: "Mas de 50 empleados", price: "$100,000 MXN/ano" },
                ].map((tier, i) => (
                  <div key={i} className="flex items-center justify-between p-4 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:bg-white/[0.06] transition-all">
                    <div className="flex items-center gap-3">
                      <Palette className="w-5 h-5 text-violet-400" />
                      <span className="text-white font-medium text-sm">{tier.label}</span>
                    </div>
                    <span className="text-violet-400 font-bold text-sm">{tier.price}</span>
                  </div>
                ))}
              </div>
              <div className="mt-8 flex flex-wrap gap-2">
                {["Todos los modulos Pro", "Dominio personalizado", "Logo y colores propios", "Agentes de IA", "Soporte prioritario"].map((f, i) => (
                  <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/[0.04] text-slate-300 text-xs border border-white/[0.08]">
                    <Check className="w-3 h-3 text-emerald-400" />{f}
                  </span>
                ))}
              </div>
              <div className="mt-8">
                <Link to="/register">
                  <Button size="lg" className="bg-violet-600 hover:bg-violet-500 rounded-full h-12 px-8 shadow-lg shadow-violet-600/20 transition-all hover:scale-[1.03]" data-testid="wl-cta-btn">
                    Solicitar White Label <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            {/* Visual mockup */}
            <div className="relative">
              <div className="absolute inset-0 bg-violet-500/10 rounded-3xl blur-3xl scale-90" />
              <div className="relative bg-[#161228] rounded-2xl p-6 border border-white/[0.08] shadow-2xl">
                <div className="flex items-center gap-2 mb-5">
                  <div className="w-3 h-3 rounded-full bg-red-500/80" /><div className="w-3 h-3 rounded-full bg-yellow-500/80" /><div className="w-3 h-3 rounded-full bg-green-500/80" />
                  <span className="ml-3 text-[11px] text-slate-500 font-mono">rh.tuempresa.com</span>
                </div>
                <div className="p-5 bg-gradient-to-br from-blue-600 to-blue-800 rounded-xl mb-4">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center shadow"><Star className="w-5 h-5 text-white" /></div>
                    <div><span className="text-white font-bold text-base">Tu Empresa RH</span><p className="text-blue-200 text-xs">Panel de administracion</p></div>
                  </div>
                  <div className="grid grid-cols-3 gap-2">
                    {[{ v: "128", l: "Empleados" }, { v: "95%", l: "Asistencia" }, { v: "$890K", l: "Nomina" }].map((s, i) => (
                      <div key={i} className="bg-white/10 rounded-lg p-2 text-center backdrop-blur-sm">
                        <p className="text-white font-bold text-sm">{s.v}</p>
                        <p className="text-blue-200 text-[10px]">{s.l}</p>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {["Empleados", "Nomina", "Analytics"].map((m, i) => (
                    <div key={i} className="p-3 rounded-lg bg-white/[0.04] border border-white/[0.06] text-center hover:bg-white/[0.08] transition-all">
                      <p className="text-xs text-slate-300 font-medium">{m}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ MOBILE APP SECTION ============ */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Phone mockup */}
            <div className="relative flex justify-center order-2 lg:order-1">
              <div className="relative w-[280px]">
                {/* Phone frame */}
                <div className="bg-slate-900 rounded-[2.5rem] p-3 shadow-2xl">
                  <div className="bg-[#fafbfc] rounded-[2rem] overflow-hidden">
                    {/* Status bar */}
                    <div className="bg-white px-5 py-3 flex items-center justify-between border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <img src="/logo-humanoz.png" alt="Logo" className="h-6 w-auto" />
                      </div>
                      <span className="text-[9px] text-slate-400">Salir</span>
                    </div>
                    {/* Employee info */}
                    <div className="px-5 pt-4 pb-2">
                      <p className="text-sm font-bold text-slate-900">Maria Gonzalez</p>
                      <p className="text-[10px] text-slate-500">Gerente de Ventas</p>
                    </div>
                    {/* GPS badge */}
                    <div className="px-5 mb-3">
                      <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-emerald-50 text-emerald-700 text-[10px] font-medium">
                        <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                        GPS activo (precision: 8m)
                      </div>
                    </div>
                    {/* Today card */}
                    <div className="px-5 mb-3">
                      <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
                        <p className="text-[9px] text-slate-400 uppercase tracking-wider font-semibold text-center mb-2">Martes, 26 de marzo</p>
                        <div className="flex items-center justify-center gap-6">
                          <div className="text-center"><p className="text-[9px] text-slate-400">Entrada</p><p className="text-lg font-bold text-slate-900 font-mono">08:57</p></div>
                          <ChevronRight className="w-4 h-4 text-slate-300" />
                          <div className="text-center"><p className="text-[9px] text-slate-400">Salida</p><p className="text-lg font-bold text-slate-300 font-mono">--:--</p></div>
                        </div>
                        <div className="flex justify-center mt-2"><span className="text-[9px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full font-semibold">A tiempo</span></div>
                      </div>
                    </div>
                    {/* Clock out button */}
                    <div className="px-5 pb-5">
                      <div className="bg-slate-800 text-white text-center py-3.5 rounded-xl text-sm font-bold shadow-lg">Registrar Salida</div>
                    </div>
                  </div>
                </div>
                {/* Notch */}
                <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 bg-slate-900 rounded-b-2xl" />
              </div>
            </div>
            {/* Text content */}
            <div className="order-1 lg:order-2">
              <Badge className="bg-blue-100 text-blue-700 border-blue-200 mb-4 font-semibold">App Movil</Badge>
              <h2 className="text-3xl md:text-4xl font-extrabold text-slate-900 tracking-tight mb-4">
                Asistencia desde el celular<br />
                <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">con GPS y Geofencing</span>
              </h2>
              <p className="text-base text-slate-500 mb-6 leading-relaxed">Tus empleados registran entrada y salida desde su celular. El GPS verifica automaticamente que estan en la ubicacion correcta.</p>
              <div className="space-y-3 mb-8">
                {[
                  { icon: MapPin, text: "Geofencing: Solo permite registro dentro del radio configurado" },
                  { icon: Smartphone, text: "Instalable como app nativa en Android e iOS" },
                  { icon: Palette, text: "White Label: Tu logo y colores en la app de tus clientes" },
                  { icon: Shield, text: "Datos encriptados y conexion segura" },
                ].map((f, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center shrink-0"><f.icon className="w-4 h-4 text-blue-600" /></div>
                    <p className="text-sm text-slate-700 pt-1">{f.text}</p>
                  </div>
                ))}
              </div>
              <div className="flex flex-col sm:flex-row gap-3">
                <a href="/mobile" target="_blank" rel="noopener noreferrer">
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 rounded-full h-12 px-8 shadow-lg shadow-blue-600/20 font-semibold transition-all hover:scale-[1.03]" data-testid="landing-download-app-btn">
                    <Download className="w-5 h-5 mr-2" />Abrir App Movil
                  </Button>
                </a>
                <Link to="/register">
                  <Button size="lg" variant="outline" className="rounded-full h-12 px-8 font-semibold border-slate-300">
                    Crear Cuenta Gratis
                  </Button>
                </Link>
              </div>
              <p className="text-xs text-slate-400 mt-4">Abre el link desde tu celular y toca "Agregar a pantalla de inicio" para instalar</p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ FINAL CTA ============ */}
      <section className="py-24 relative overflow-hidden" style={{ background: "linear-gradient(135deg, #059669 0%, #10b981 50%, #047857 100%)" }}>
        <div className="absolute inset-0"><div className="absolute top-0 right-0 w-[400px] h-[400px] bg-white/5 rounded-full blur-[100px]" /><div className="absolute bottom-0 left-0 w-[300px] h-[300px] bg-black/5 rounded-full blur-[80px]" /></div>
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 text-white text-xs font-semibold mb-6 backdrop-blur-sm">
            <Clock className="w-3.5 h-3.5" />
            Oferta de lanzamiento
          </div>
          <h2 className="text-3xl md:text-4xl font-extrabold text-white tracking-tight mb-4">
            15 dias gratis. Todos los modulos.<br />Cero riesgo.
          </h2>
          <p className="text-base text-emerald-50/80 mb-3 max-w-xl mx-auto">
            Activa tu cuenta ahora y accede al plan Pro completo con 20 modulos y 4 agentes de IA. <strong className="text-white">Sin tarjeta de credito.</strong>
          </p>
          <p className="text-emerald-200/60 text-sm mb-8">Si no te convence, simplemente no continues. Sin compromisos.</p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-emerald-700 hover:bg-emerald-50 font-bold h-14 px-10 rounded-full text-lg shadow-xl transition-all hover:scale-[1.03] active:scale-95" data-testid="cta-final-btn">
              Activar Prueba Gratis Ahora
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
          <div className="flex items-center justify-center gap-6 mt-8 text-sm text-emerald-100/80">
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4" />Sin tarjeta</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4" />Plan Pro completo</span>
            <span className="flex items-center gap-1.5"><Check className="w-4 h-4" />Soporte incluido</span>
          </div>
        </div>
      </section>

      {/* ============ FOOTER ============ */}
      <footer className="py-12 bg-slate-950" data-testid="landing-footer">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src="/logo-humanoz.png" alt="Human-OZ" className="h-8 w-auto mb-4" />
              <p className="text-sm text-slate-400 leading-relaxed">Software de Recursos Humanos con Inteligencia Artificial para Mexico.</p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-3 text-sm">Producto</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><a href="#modulos" className="hover:text-white transition-colors">Modulos</a></li>
                <li><a href="#precios" className="hover:text-white transition-colors">Precios</a></li>
                <li><a href="#ia" className="hover:text-white transition-colors">Inteligencia Artificial</a></li>
                <li><a href="#whitelabel" className="hover:text-white transition-colors">White Label</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-3 text-sm">Recursos</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li><Link to="/register" className="hover:text-white transition-colors">Prueba Gratis</Link></li>
                <li><Link to="/login" className="hover:text-white transition-colors">Iniciar Sesion</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-3 text-sm">Contacto</h4>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>contacto@humanoz.click</li>
                <li>CDMX, Mexico</li>
              </ul>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-slate-500">&copy; {new Date().getFullYear()} Human-OZ. Todos los derechos reservados.</p>
            <div className="flex gap-4 text-sm text-slate-500">
              <a href="#" className="hover:text-white transition-colors">Privacidad</a>
              <a href="#" className="hover:text-white transition-colors">Terminos</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
